/**
 * ProPlusFeatureCarousel — iter87.199.76
 *
 * 6-slide horizontal scroll-snap carousel highlighting the Pro+
 * features that convert Free users. Layout & interaction vocabulary
 * mirrors `HowItWorksCarousel` on `/` — hidden native scrollbar,
 * page dots, 4s auto-advance with pause-on-interact, keyboard arrows.
 *
 * Copy locked with operator on 2026-02-16 (see UAT_iter87.199.75.md):
 *
 *   1. As many rivalries as you'd like
 *   2. 5 tournaments per rivalry
 *   3. Your AI Friend (Rivalries Intel + Season Recap combined)
 *   4. Trophy Tokens — track who's in and who's out
 *   5. Multi-crew hub — every rivalry in one place
 *   6. Rivalries live forever
 *
 * iter87.199.76 — Operator design pass:
 *   - Scrollbar hidden (was leaking on desktop)
 *   - Added page dots below strip (cyber accent for active)
 *   - Added 4s auto-advance with pause-on-hover / touch + reduced-motion gate
 */
import React from "react";
import WideScreenshotCard, { CARD_WIDTH } from "./WideScreenshotCard";
import RideStoryMock from "./RideStoryMock";
import GameTypeToggleMock from "./GameTypeToggleMock";

// iter87.515 — Unified all 4 slides onto `WideScreenshotCard` (the
// "foldable phone" wide-short frame) per operator ask — the mixed
// tall-phone / wide-card sizing looked inconsistent.
// iter87.516 — Operator flagged the top-cropped Rivalries Intel slide
// was hiding "Bot Says" (the sharp/trap picks, lower on that tall
// capture). That slide now renders at its OWN real aspect ratio
// (840/1740) but height-matched to the other cards instead of width-
// matched — narrower, but the full capture shows uncropped. A themed
// Ride Story card (see `RideStoryMock.jsx`) peeks behind it.
// iter87.520 — Reordered so "Never miss a to-do" is the 3rd slide.
// Trophy Tokens now uses the same `fullHeight` treatment as the AI
// Helper slide (full real screenshot, uncropped, taller/narrower) so
// its "Game Type" section is visible at all.
// iter87.522 — Replaced Trophy Tokens' mock peek with a REAL
// screenshot of the live app's Podium config, seeded + captured via
// a throwaway fixture (see `tests/seed_iter87_522_trophy_podium_
// screenshot.py`, cleaned up after capture — not left in the DB).
const SLIDES = [
    {
        key: "one-place", tone: "blaze", dotColor: "bg-blaze",
        caption: "One place. Unlimited rivalries.",
        sub: "Every crew, every friend circle — one hub.",
        img: "/screens/pro/01-pro-hub.png",
        alt: "Rivalry Pro+ hub showing every rivalry you run and play in",
    },
    {
        key: "keep-going", tone: "cyber", dotColor: "bg-cyber",
        caption: "Keep every rivalry going.",
        sub: "5 live tournaments · Trophy Tokens across seasons.",
        img: "/screens/pro/02-five-tournaments.png",
        alt: "Five tournaments running inside one rivalry",
    },
    {
        key: "todos", tone: "neon", dotColor: "bg-neon",
        caption: "Never miss a to-do.",
        sub: "Email reminders land the moment something's waiting on you.",
        img: "/screens/pro/01b-pro-hub-todos-reminders.png",
        alt: "Pro+ hub with to-do email reminders and things waiting on you",
    },
    {
        key: "in-app", tone: "volt", dotColor: "bg-volt", fullHeight: true,
        caption: "Your AI Helper — sort of...",
        sub: "Science Says rankings, Bot Says picks, and your season story — all from one place.",
        img: "/screens/pro/03-rivalries-intel-ai-picks.png",
        alt: "Rivalries Intel AI Friend weekly picks screen, including the Bot Says sharp and trap picks",
        SecondaryMock: RideStoryMock,
    },
    {
        key: "trophy-tokens", tone: "heat", dotColor: "bg-heat", fullHeight: true,
        caption: "Trophy Tokens — track who's in, who's out.",
        sub: "Season-long pots and eliminations, plus Winner-Take-All vs. Podium scoring.",
        img: "/screens/pro/04-trophy-tokens.png",
        alt: "Trophy Tokens configuration screen for season-long pots, eliminations and game type",
        SecondaryMock: GameTypeToggleMock,
    },
];

const AUTO_ADVANCE_MS = 4000;
const RESUME_AFTER_INTERACTION_MS = 6000;

export default function ProPlusFeatureCarousel() {
    const scrollerRef = React.useRef(null);
    const [active, setActive] = React.useState(0);
    const [paused, setPaused] = React.useState(false);
    const resumeTimerRef = React.useRef(null);

    const [reducedMotion] = React.useState(() =>
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    );

    const goTo = React.useCallback((idx, opts = {}) => {
        const n = SLIDES.length;
        const next = ((idx % n) + n) % n;
        const el = scrollerRef.current;
        if (!el) return;
        const child = el.children[next];
        if (child) {
            el.scrollTo({
                left: child.offsetLeft - (el.clientWidth - child.clientWidth) / 2,
                behavior: opts.smooth ? "smooth" : "auto",
            });
        }
        setActive(next);
    }, []);

    React.useEffect(() => {
        if (paused || reducedMotion) return;
        const t = setInterval(() => {
            setActive((cur) => {
                const next = (cur + 1) % SLIDES.length;
                const el = scrollerRef.current;
                const child = el?.children?.[next];
                if (el && child) {
                    el.scrollTo({
                        left: child.offsetLeft - (el.clientWidth - child.clientWidth) / 2,
                        behavior: "smooth",
                    });
                }
                return next;
            });
        }, AUTO_ADVANCE_MS);
        return () => clearInterval(t);
    }, [paused, reducedMotion]);

    React.useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        // iter87.199.128 — IntersectionObserver fires its FIRST callback
        // synchronously with whatever the browser has painted so far,
        // which on mount (before scroll-snap layout has settled) can
        // report several/all slides as simultaneously visible. Sorting
        // by intersectionRatio among that noisy initial batch could pick
        // the LAST slide instead of slide 0 — the dot indicator and the
        // scroll position start out of sync, then the 4s auto-advance
        // timer (which increments off that wrong `active`) makes it jump
        // around. Deferring observer creation one frame lets layout
        // settle first, so the initial fire correctly reports only
        // slide 0 as visible.
        let io;
        const raf = requestAnimationFrame(() => {
            io = new IntersectionObserver(
                (entries) => {
                    const visible = entries.filter((e) => e.isIntersecting)
                        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
                    if (visible) {
                        const idx = Number(visible.target.getAttribute("data-slide-idx"));
                        if (!Number.isNaN(idx)) setActive(idx);
                    }
                },
                { root: el, threshold: [0.55, 0.75, 0.95] }
            );
            Array.from(el.children).forEach((c) => io.observe(c));
        });
        return () => { cancelAnimationFrame(raf); io?.disconnect(); };
    }, []);

    const pauseAndArmResume = React.useCallback(() => {
        setPaused(true);
        if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = setTimeout(() => setPaused(false), RESUME_AFTER_INTERACTION_MS);
    }, []);

    React.useEffect(() => () => {
        if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    }, []);

    const onKeyDown = (e) => {
        if (e.key === "ArrowRight") { e.preventDefault(); goTo(active + 1, { smooth: true }); pauseAndArmResume(); }
        if (e.key === "ArrowLeft")  { e.preventDefault(); goTo(active - 1, { smooth: true }); pauseAndArmResume(); }
    };

    return (
        <section
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pb-16"
            aria-label="What Pro+ unlocks"
            data-testid="pro-plus-feature-carousel"
        >
            <div className="mb-6">
                <p className="text-[10px] uppercase tracking-[0.3em] text-volt font-black mb-2">
                    What Pro+ unlocks
                </p>
                <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">
                    Five ways Pro+ pays for itself.
                </h2>
            </div>

            {/* Scroll strip — scrollbar hidden across browsers, matches HowItWorksCarousel */}
            <div
                ref={scrollerRef}
                data-testid="pro-plus-carousel-scroller"
                tabIndex={0}
                role="region"
                aria-roledescription="carousel"
                onKeyDown={onKeyDown}
                onMouseEnter={pauseAndArmResume}
                onTouchStart={pauseAndArmResume}
                className="flex gap-6 overflow-x-auto snap-x snap-mandatory py-4 -mx-4 sm:-mx-6 lg:-mx-12 px-4 sm:px-6 lg:px-12
                           scrollbar-none [-ms-overflow-style:none] [scrollbar-width:none]
                           [&::-webkit-scrollbar]:hidden focus:outline-none"
                style={{ scrollBehavior: "smooth", WebkitOverflowScrolling: "touch" }}
            >
                {SLIDES.map((s, i) => {
                    const mainCard = s.fullHeight ? (
                        <WideScreenshotCard tone={s.tone} label={s.key} aspect="840 / 1740" sizeBy="height">
                            <img
                                src={s.img}
                                alt={s.alt}
                                loading="lazy"
                                className="w-full h-full object-cover object-center"
                                data-testid={`pro-plus-carousel-screenshot-${s.key}`}
                            />
                        </WideScreenshotCard>
                    ) : (
                        <WideScreenshotCard tone={s.tone} label={s.key}>
                            <img
                                src={s.img}
                                alt={s.alt}
                                loading="lazy"
                                className="w-full h-full object-cover object-top"
                                data-testid={`pro-plus-carousel-screenshot-${s.key}`}
                            />
                        </WideScreenshotCard>
                    );
                    return (
                        <div
                            key={s.key}
                            data-slide-idx={i}
                            data-testid={`pro-plus-carousel-slide-${s.key}`}
                            className="snap-center shrink-0 flex flex-col items-center gap-4"
                            aria-roledescription="slide"
                            aria-label={`${i + 1} of ${SLIDES.length}: ${s.caption}`}
                        >
                            {s.fullHeight ? (
                                <div className="relative" style={{ width: CARD_WIDTH }}>
                                    {/* iter87.516 — secondary mock, layered behind,
                                        deliberately unobtrusive (lower opacity, rotated,
                                        mostly off to the side) — just signals the extra
                                        screen exists, per operator note "doesn't have to
                                        be prominent". iter87.517 — wrapper now uses the
                                        SAME width as every other card (not a narrow custom
                                        offset), so this slide's total footprint matches
                                        the rest of the carousel. iter87.520 — bugfix: this
                                        was hardcoded to always render `RideStoryMock`, so
                                        the Trophy Tokens slide wrongly showed the Ride
                                        Story peek too instead of its own Game Type toggle
                                        peek. iter87.522 — Trophy Tokens' peek is now a
                                        REAL screenshot of the live Podium config (not a
                                        mock), so `secondaryImg` renders an `<img>` while
                                        `SecondaryMock` (still used by the AI Helper slide)
                                        renders a component — and each secondary can set
                                        its own real aspect ratio instead of assuming the
                                        phone shape. iter87.523 — the real screenshot's text
                                        was captured at normal CSS pixel density (not the
                                        ~2x the original supplied assets used), so at the
                                        shared 0.74 peek scale it rendered noticeably bigger/
                                        blurrier than the main card and the mock's text.
                                        `secondaryScale` lets a slide override that scale
                                        per-slide instead of forcing every peek to the same
                                        number. */}
                                    {(s.secondaryImg || s.SecondaryMock) && (
                                        <div
                                            className="absolute top-10 right-0 opacity-70 z-0"
                                            style={{ transform: `rotate(6deg) scale(${s.secondaryScale ?? 0.74})` }}
                                        >
                                            <WideScreenshotCard tone={s.tone} label={`${s.key}-secondary`} aspect={s.secondaryAspect || "840 / 1740"} sizeBy="height">
                                                {s.secondaryImg ? (
                                                    <img
                                                        src={s.secondaryImg}
                                                        alt={s.secondaryAlt}
                                                        loading="lazy"
                                                        className="w-full h-full object-cover object-center"
                                                        data-testid={`pro-plus-carousel-screenshot-${s.key}-secondary`}
                                                    />
                                                ) : (
                                                    <s.SecondaryMock />
                                                )}
                                            </WideScreenshotCard>
                                        </div>
                                    )}
                                    <div className="relative z-10">
                                        {mainCard}
                                    </div>
                                </div>
                            ) : mainCard}
                            <div
                                data-testid={`pro-plus-carousel-caption-${s.key}`}
                                className="max-w-[280px] text-center"
                            >
                                <div className="text-[10px] font-mono font-black uppercase tracking-widest text-zinc-500 mb-1">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <div className="text-sm sm:text-base font-bold text-white leading-snug">
                                    {s.caption}
                                </div>
                                {s.sub && (
                                    <div className="text-[11px] text-zinc-400 leading-snug mt-1">
                                        {s.sub}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Page dots */}
            <div
                data-testid="pro-plus-carousel-dots"
                className="flex items-center justify-center gap-1.5 mt-6"
                role="tablist"
            >
                {SLIDES.map((s, i) => (
                    <button
                        key={s.key}
                        role="tab"
                        aria-selected={i === active}
                        aria-label={`Go to slide ${i + 1}`}
                        data-testid={`pro-plus-carousel-dot-${s.key}`}
                        onClick={() => { goTo(i, { smooth: true }); pauseAndArmResume(); }}
                        className={`h-1.5 rounded-full transition-all ${
                            i === active
                                ? `w-6 ${SLIDES[active].dotColor}`
                                : "w-1.5 bg-white/20 hover:bg-white/40"
                        }`}
                    />
                ))}
            </div>
        </section>
    );
}
